/// <reference types="vite/client" />
// ─────────────────────────────────────────────────────
// Wardrobe Page — Real garment images + Try On trigger
// ─────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { Search, Filter, Plus, Heart, Camera, X, Loader2, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import { api } from "../services/api";

const CATEGORIES = ["All", "Tops", "Bottoms", "Dresses", "Outerwear", "Shoes", "Accessories"];

const CATEGORY_EMOJI: Record<string, string> = {
  Tops: "👔", Bottoms: "👖", Dresses: "👗", Outerwear: "🧥",
  Shoes: "👟", Accessories: "🧣",
};

type Garment = {
  id: string;
  name: string;
  brand?: string;
  category: string;
  color?: string;
  image_url?: string;
  price_cents?: number;
  size_range?: Record<string, unknown>;
  wearCount?: number;
  matchScore?: number;
  favorite?: boolean;
};

function GarmentCard({
  garment,
  onToggleFavorite,
  onTryOn,
}: {
  garment: Garment;
  onToggleFavorite: (id: string) => void;
  onTryOn: (garment: Garment) => void;
}) {
  const [imgErr, setImgErr] = useState(false);

  return (
    <div className="group bg-sxt-surface rounded-2xl border overflow-hidden hover:border-sxt-primary/50 transition-all animate-fade-in flex flex-col">
      {/* Image */}
      <div className="relative aspect-[3/4] bg-white/5 overflow-hidden">
        {garment.image_url && !imgErr ? (
          <img
            src={garment.image_url}
            alt={garment.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={() => setImgErr(true)}
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ backgroundColor: (garment.color || "#333") + "25" }}
          >
            <span className="text-5xl">
              {CATEGORY_EMOJI[garment.category] || "👕"}
            </span>
          </div>
        )}

        {/* Overlay actions — show on hover */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-end justify-center pb-3 opacity-0 group-hover:opacity-100">
          <button
            onClick={() => onTryOn(garment)}
            className="flex items-center gap-1.5 bg-sxt-primary text-black text-xs font-bold px-3 py-2 rounded-full shadow-lg hover:bg-sxt-primary/90 transition-all"
          >
            <Camera className="w-3.5 h-3.5" />
            Try On
          </button>
        </div>

        {/* Favorite */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleFavorite(garment.id); }}
          className="absolute top-2 right-2 p-1.5 rounded-full bg-black/30 backdrop-blur-sm"
        >
          <Heart className={clsx("w-3.5 h-3.5", garment.favorite ? "text-red-400 fill-red-400" : "text-white/70 hover:text-red-400")} />
        </button>

        {/* Match score badge */}
        {(garment.matchScore || 0) > 0 && (
          <div className="absolute top-2 left-2 bg-sxt-primary/90 text-black text-xs font-bold px-2 py-0.5 rounded-full">
            {garment.matchScore}%
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-3 flex-1 flex flex-col gap-1">
        <p className="text-sm font-semibold truncate">{garment.name}</p>
        {garment.brand && (
          <p className="text-xs text-sxt-muted truncate">{garment.brand}</p>
        )}
        <div className="flex items-center justify-between mt-auto pt-1">
          <span className="text-xs text-sxt-dim">{garment.category}</span>
          {garment.price_cents ? (
            <span className="text-xs font-bold text-sxt-primary">
              ${(garment.price_cents / 100).toFixed(0)}
            </span>
          ) : (
            <span className="text-xs text-sxt-dim">Worn {garment.wearCount || 0}×</span>
          )}
        </div>
      </div>

      {/* Try On button — always visible on mobile */}
      <button
        onClick={() => onTryOn(garment)}
        className="md:hidden flex items-center justify-center gap-2 py-2.5 bg-sxt-primary/10 border-t border-sxt-primary/20 text-sxt-primary text-xs font-bold hover:bg-sxt-primary/20 transition-all"
      >
        <Camera className="w-3.5 h-3.5" />
        Try On
      </button>
    </div>
  );
}

function AddGarmentModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Tops");
  const [color, setColor] = useState("#333333");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.addGarment({ name, category, color });
      onAdded();
      onClose();
    } catch {
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-md rounded-2xl bg-sxt-surface p-6 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold">Add Garment</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-sxt-muted" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm text-sxt-muted">Name</label>
            <input required value={name} onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white outline-none focus:border-sxt-primary" />
          </div>
          <div>
            <label className="mb-1 block text-sm text-sxt-muted">Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded-lg border border-white/10 bg-sxt-bg px-4 py-3 text-white outline-none focus:border-sxt-primary">
              {CATEGORIES.filter(c => c !== "All").map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm text-sxt-muted">Color</label>
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
              className="w-16 h-10 rounded cursor-pointer" />
          </div>
          <button type="submit" disabled={saving}
            className="w-full rounded-xl bg-sxt-primary py-3 font-bold text-black transition hover:opacity-90 disabled:opacity-50">
            {saving ? "Saving…" : "Add to Wardrobe"}
          </button>
        </form>
      </div>
    </div>
  );
}

export function WardrobePage() {
  const navigate = useNavigate();
  const [garments, setGarments] = useState<Garment[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  const fetchWardrobe = async () => {
    setLoading(true);
    try {
      // Try catalog first, then wardrobe
      let raw: unknown[] = [];
      try {
        const data = await api.getProducts();
        raw = Array.isArray(data) ? data : (data as { items?: unknown[] })?.items || [];
      } catch {
        const data = await api.getWardrobe(category === "All" ? undefined : category);
        raw = Array.isArray(data) ? data : [];
      }

      const items: Garment[] = raw.map((g: unknown) => {
        const r = g as Record<string, unknown>;
        return {
          id: (r.id as string) || String(Math.random()),
          name: (r.name as string) || "Unnamed",
          brand: (r.brand as string) || undefined,
          category: (r.category as string) || "Tops",
          color: (r.color_primary as string) || (r.color as string) || "#6C63FF",
          image_url: (r.image_url as string) || undefined,
          price_cents: (r.price_cents as number) || undefined,
          size_range: (r.size_range as Record<string, unknown>) || undefined,
          wearCount: ((r.wear_count ?? r.wearCount ?? 0) as number),
          matchScore: ((r.match_score ?? r.matchScore ?? 0) as number),
          favorite: Boolean(r.favorite ?? r.is_favorite),
        };
      });
      setGarments(items);
    } catch {
      setGarments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchWardrobe(); }, [category]);

  const toggleFavorite = (id: string) => {
    setGarments(prev => prev.map(g => g.id === id ? { ...g, favorite: !g.favorite } : g));
  };

  // Navigate to Try-On page with garment pre-selected via localStorage
  const handleTryOn = (garment: Garment) => {
    localStorage.setItem("sxt_tryon_garment", JSON.stringify(garment));
    navigate("/tryon");
  };

  const filtered = garments.filter(g =>
    (category === "All" || g.category.toLowerCase() === category.toLowerCase()) &&
    g.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-bold">Wardrobe</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/tryon")}
            className="flex items-center gap-2 bg-sxt-primary hover:bg-sxt-primary/90 text-black px-4 py-2.5 rounded-xl text-sm font-bold transition-colors"
          >
            <Camera className="w-4 h-4" />
            Try On
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 bg-sxt-surface border hover:bg-white/5 text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-sxt-muted" />
          <input
            type="text"
            placeholder="Search wardrobe…"
            className="w-full bg-sxt-surface border rounded-xl pl-11 pr-4 py-3 text-sm text-sxt-text placeholder:text-sxt-dim focus:outline-none focus:border-sxt-primary"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button className="flex items-center gap-2 bg-sxt-surface border rounded-xl px-4 py-3 text-sm text-sxt-muted hover:text-sxt-text transition-colors">
          <Filter className="w-4 h-4" />
          Filter
        </button>
      </div>

      {/* Category chips */}
      <div className="flex gap-2 mb-8 overflow-x-auto pb-1">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            className={clsx(
              "px-4 py-2 rounded-full text-sm font-medium shrink-0 transition-colors border",
              category === c
                ? "bg-sxt-primary/15 text-sxt-primary border-sxt-primary"
                : "text-sxt-muted border-sxt-border hover:text-sxt-text hover:bg-sxt-surface"
            )}
          >
            {c}
          </button>
        ))}
      </div>

      <p className="text-sm text-sxt-muted mb-4">{filtered.length} items</p>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-sxt-primary animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <Star className="w-12 h-12 text-sxt-dim mx-auto mb-4" />
          <p className="text-lg text-sxt-muted">No garments found</p>
          <p className="text-sm text-sxt-dim mt-1">Try a different category or add your first garment</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filtered.map((g) => (
            <GarmentCard
              key={g.id}
              garment={g}
              onToggleFavorite={toggleFavorite}
              onTryOn={handleTryOn}
            />
          ))}
        </div>
      )}

      {showAdd && <AddGarmentModal onClose={() => setShowAdd(false)} onAdded={fetchWardrobe} />}
    </div>
  );
}
